import { Menu, type MenuProps, Popconfirm } from "antd"
import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import styled from "styled-components"

import Flex from "#blocks/Flex"
import SingleClickButton from "#blocks/SingleClickButton"
import { getFirebaseEnv, setFirebaseEnv } from "#classes/Firebase"
import { MENU_ITEMS, MENU_VIEW_ITEMS } from "#constants/menuOptions"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import { useRouteAccessChecker } from "#hooks/newHooks/useHasPermission"
import { camelToWords } from "#utils/textFormatters"

import ImpersonationBanner from "./ImpersonationBanner"
import RoleOverrideBanner from "./RoleOverrideBanner"
import UserMenu from "./UserMenu"

export const HEADER_HEIGHT = 50

const SHeader = styled.div`
  width: 100vw;
  padding: 0 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
  background-color: ${({ theme }) => theme.mainColor};

  && li.ant-menu-item-selected,
  && li.ant-menu-item-active {
    color: ${({ theme }) => theme.white} !important;

    ::after {
      border-bottom: 2px solid ${({ theme }) => theme.white} !important;
    }
    /* For some reason, this needs to be separate */
    ::after:hover {
      border-bottom: 2px solid ${({ theme }) => theme.white} !important;
    }
  }
`

// Helper function to create menu items with nested structure
const createMenuItems = (
  menuItems: typeof MENU_ITEMS | typeof MENU_VIEW_ITEMS,
  canAccessRoute: (route: string) => boolean
): MenuProps["items"] => {
  return Object.entries(menuItems)
    .map(([key, item]) => {
      if (typeof item === "string") {
        if (!canAccessRoute(item)) {
          return null
        }

        // Simple menu item
        return {
          label: camelToWords(key),
          key: item,
        }
      } else if (item.children) {
        // Nested menu item
        const children = Object.entries(item.children)
          .map(([childKey, childPath]) => {
            if (typeof childPath !== "string" || !canAccessRoute(childPath)) {
              return null
            }

            return {
              label: camelToWords(childKey),
              key: childPath,
            }
          })
          .filter(Boolean)

        if (!children.length) {
          return null
        }

        return {
          label: item.label,
          key: key,
          children,
        }
      }
      return null
    })
    .filter(Boolean)
}

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const canAccessRoute = useRouteAccessChecker()

  const items: MenuProps["items"] = createMenuItems(MENU_ITEMS, canAccessRoute)
  const viewItems: MenuProps["items"] = createMenuItems(
    MENU_VIEW_ITEMS,
    canAccessRoute
  )

  const { currentUserId, currentUser, setUserSetting } = useUserContext()
  const isDev = currentUser?.role === USER_ROLES.DEV
  const firebaseEnv = isDev ? getFirebaseEnv() : null

  const onMenuClick: MenuProps["onClick"] = (e) => {
    if (!currentUserId) return

    // Only navigate if the clicked item is a route (starts with "/")
    if (e.key.startsWith("/")) {
      setUserSetting("lastPage", e.key)
      void navigate(e.key)
    }
  }

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys)
  }

  const theme = useThemeContext()

  const { onPinLogout } = useUserContext()

  return (
    <>
      <ImpersonationBanner />
      <RoleOverrideBanner />
      <SHeader theme={theme}>
        <Flex style={{ width: "60vw", justifyContent: "center" }} gap={60}>
          {/* Left side tabs */}
          <Menu
            data-testid="primary-nav"
            onClick={onMenuClick}
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            mode="horizontal"
            items={items}
            style={{
              width: 600,
              justifyContent: "center",
            }}
          />

          {/* Front desk and teacher view */}
          <Menu
            data-testid="view-nav"
            onClick={onMenuClick}
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            mode="horizontal"
            items={viewItems}
            style={{
              width: 350,
              justifyContent: "center",
            }}
          />

          {/* Logout */}
          <Flex center>
            <SingleClickButton
              onClick={onPinLogout}
              type="text"
              style={{
                color: theme.white,
                fontSize: 14,
                border: "1px solid white",
                fontWeight: 600,
                width: 120,
              }}
            >
              Log out
            </SingleClickButton>
          </Flex>
        </Flex>

        {/* Right side environment and user menu */}
        <Flex
          style={{
            width: "30vw",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
          gap={12}
        >
          {firebaseEnv && (
            <Popconfirm
              title={`Switch to ${firebaseEnv === "PROD" ? "DEV" : "PROD"}?`}
              description="This will reload the page."
              onConfirm={() =>
                setFirebaseEnv(firebaseEnv === "PROD" ? "DEV" : "PROD")
              }
              okText="Switch"
              cancelText="Cancel"
              okButtonProps={{
                danger: firebaseEnv === "DEV",
              }}
            >
              <span
                style={{
                  padding: "2px 10px",
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: "#fff",
                  cursor: "pointer",
                  backgroundColor:
                    firebaseEnv === "PROD" ? theme.error : theme.success,
                }}
              >
                {firebaseEnv}
              </span>
            </Popconfirm>
          )}
          <UserMenu />
        </Flex>
      </SHeader>
    </>
  )
}

export default Header
